/* eslint-disable no-mixed-operators */

import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import Promise from 'bluebird';
import { focusLockInstanceDecorator } from '@bingads-webui/focus-lock-decorator';
import controlTemplate from '../template/popup.pug';

function getDimensions(element) {
  const $element = $(element);

  return {
    width: $element.outerWidth(),
    height: $element.outerHeight(),
  };
}

function getBoundingRectPosition(element) {
  const position = element.position();
  const { width, height } = getDimensions(element);

  return {
    top: position.top,
    left: position.left,
    width,
    height,
  };
}

function getBoundingRectOffset(element) {
  const position = element.offset();
  const { width, height } = getDimensions(element);

  return {
    top: position.top,
    left: position.left,
    width,
    height,
  };
}

function testFit(containerDimensions, elementDimentions, position) {
  // Calculating how much of the element intersects with the container
  const verticalFit = Math.max(
    0,
    Math.min(containerDimensions.height, position.top + elementDimentions.height) -
    Math.max(0, position.top)
  ) / elementDimentions.height;

  const horizontalFit = Math.max(
    0,
    Math.min(containerDimensions.width, position.left + elementDimentions.width) -
    Math.max(0, position.left)
  ) / elementDimentions.width;

  return {
    verticalFit,
    horizontalFit,
    get areaFit() {
      return verticalFit * horizontalFit;
    },
  };
}

export const FixedPositionLocation = Object.freeze({
  buttomLeft: 'buttomLeft',
  topRight: 'topRight',
  topLeft: 'topLeft',
  buttomRight: 'buttomRight',
  right: 'right',
  left: 'left',
  top: 'top',
  bottom: 'bottom',
});

export const PositionAttribute = Object.freeze({
  fixed: 'fixed',
  absolute: 'absolute',
});

function getFixedPosition(positionOptions, container, anchor, element) {
  const popupDimensions = getDimensions(element);
  const anchorBoundingRect = positionOptions.positionAttribute === PositionAttribute.fixed ?
    getBoundingRectOffset(anchor) :
    getBoundingRectPosition(anchor);

  switch (positionOptions.location) {
    case FixedPositionLocation.buttomLeft: return {
      top: anchorBoundingRect.top + anchorBoundingRect.height,
      left: anchorBoundingRect.left + anchorBoundingRect.width - popupDimensions.width,
    };
    case FixedPositionLocation.topRight: return {
      top: anchorBoundingRect.top - popupDimensions.height,
      left: anchorBoundingRect.left,
    };
    case FixedPositionLocation.topLeft: return {
      top: anchorBoundingRect.top - popupDimensions.height,
      left: anchorBoundingRect.left + anchorBoundingRect.width - popupDimensions.width,
    };
    case FixedPositionLocation.right: return {
      top: anchorBoundingRect.top + anchorBoundingRect.height * 0.5 - popupDimensions.height * 0.5,
      left: anchorBoundingRect.left + anchorBoundingRect.width,
    };
    case FixedPositionLocation.left: return {
      top: anchorBoundingRect.top + anchorBoundingRect.height * 0.5 - popupDimensions.height * 0.5,
      left: anchorBoundingRect.left - popupDimensions.width,
    };
    case FixedPositionLocation.top: return {
      top: anchorBoundingRect.top - popupDimensions.height,
      left: anchorBoundingRect.left + anchorBoundingRect.width * 0.5 - popupDimensions.width * 0.5,
    };
    case FixedPositionLocation.bottom: return {
      top: anchorBoundingRect.top + anchorBoundingRect.height,
      left: anchorBoundingRect.left + anchorBoundingRect.width * 0.5 - popupDimensions.width * 0.5,
    };
    case FixedPositionLocation.buttomRight:
    default: return {
      top: anchorBoundingRect.top + anchorBoundingRect.height,
      left: anchorBoundingRect.left,
    };
  }
}

function getFittingPosition(positionOptions, container, anchor, element) {
  const containerDimensions = getDimensions(container);
  const popupDimensions = getDimensions(element);

  const positions = [
    getFixedPosition({ location: FixedPositionLocation.buttomRight }, container, anchor, element),
    getFixedPosition({ location: FixedPositionLocation.buttomLeft }, container, anchor, element),
    getFixedPosition({ location: FixedPositionLocation.topRight }, container, anchor, element),
    getFixedPosition({ location: FixedPositionLocation.topLeft }, container, anchor, element),
  ];

  const threshold = positionOptions && positionOptions.threshold;

  const fittingPosition =
    _.find(positions, position =>
      testFit(containerDimensions, popupDimensions, position).areaFit >= 1) ||
    threshold &&
      (_.find(positions, position =>
        testFit(containerDimensions, popupDimensions, position).areaFit >= threshold) ||
        positions[0]) ||
    _.max(positions, position => testFit(containerDimensions, popupDimensions, position).areaFit);

  return _.pick(fittingPosition, 'left', 'top');
}

export const PositioningMethod = Object.freeze({
  fixed: 'fixed',
  fit: 'fit',
});

function getPositioningMethod(positioningMethod) {
  switch (positioningMethod) {
    case PositioningMethod.fixed:
      return getFixedPosition;
    case PositioningMethod.fit:
    default:
      return getFittingPosition;
  }
}

class LabelsPopup extends Backbone.View {
  className() {
    return 'popup';
  }

  attributes() {
    return {
      role: 'dialog',
    };
  }

  get events() {
    return {
      'click .popup__close': (e) => {
        this.onClose(e);
      },
    };
  }

  get combinedEvents() {
    return _.extend(
      this.options.events,
      this.events
    );
  }

  render() {
    this.$el.addClass((this.options.classes || []).join(' '));
    this.$el.append(controlTemplate({
      contents: this.options.template(),
      closeLabel: this.options.i18n.getString('Popup_Close'),
    }));
    this.setPosition(
      this.options.positioningMethod,
      this.options.positioningOptions,
      this.options.container,
      this.options.anchor,
      this.options.positionAttr
    );
    this.delegateEvents(this.combinedEvents);

    return this;
  }

  setPosition(positioningMethod, positioningOptions, container, anchor) {
    this.$el.css({
      position: positioningOptions.positionAttribute || PositionAttribute.absolute,
      visibility: 'hidden',
    });

    // Deferring the positioning to the next cycle
    // to have the popup layed out by the browser
    _.defer(() =>
      this.$el.css(_.extend(
        {
          visibility: 'visible',
        },
        getPositioningMethod(positioningMethod)(positioningOptions, container, anchor, this.$el)
      )));
  }

  onClose(e) {
    e.stopPropagation();
    this.trigger('close');
  }
}

let currentPopup = null;
let popupInstance = null;

export function dismiss(result) {
  if (popupInstance) {
    popupInstance.trigger('close', result);
  }
}

function clickToDismiss(e) {
  if ($(e.target).closest('.popup').length === 0) {
    dismiss();
  }
}

function removePopup() {
  if (currentPopup) {
    currentPopup.remove();
    currentPopup = null;
    popupInstance = null;
    $(window).off('click', clickToDismiss);
  }
}

export function show(options) {
  if (currentPopup) {
    dismiss();
  }

  return new Promise((resolveCallback) => {
    // eslint-disable-next-line no-param-reassign
    options.container = $(options.container || 'body');

    popupInstance = new LabelsPopup(options).render();

    const FocusLockInstanceView = focusLockInstanceDecorator(
      popupInstance,
      {
        onExit: (result) => {
          resolveCallback(result);
          removePopup();
        },
        getAnchor: options.anchor ? () => options.anchor[0] : undefined,
      }, {
        eventHandlers: {
          on: {
            close: (result) => {
              resolveCallback(result);
              removePopup();
            },
          },
        },
        managed: true,
      }
    );

    currentPopup = (new FocusLockInstanceView()).render();

    options.container.append(currentPopup.el);

    // Waiting for the next cycle to not intercept the click event
    // which may have triggered this showing of the popup.
    // Otherwise, the popup will close without even showing.
    _.defer(() => {
      $(window).on('click', clickToDismiss);
      if (currentPopup) {
        currentPopup.$('.popup__close').focus();
      }
    });
  });
}

export class Popup {
  constructor({ i18n }) {
    this.popup = null;
    this.i18n = i18n;
  }

  show(options) {
    if (this.popup) {
      this.dismiss();
    }

    return new Promise((resolveCallback) => {
      // eslint-disable-next-line no-param-reassign
      options.container = $(options.container || 'body');

      this.popup = new LabelsPopup(options).render();

      const FocusLockInstanceView = focusLockInstanceDecorator(
        this.popup,
        {
          onExit: (result) => {
            resolveCallback(result);
            this.removePopup();
          },
          getAnchor: options.anchor ? () => options.anchor[0] : undefined,
        }, {
          eventHandlers: {
            on: {
              close: (result) => {
                resolveCallback(result);
                this.removePopup();
              },
            },
          },
          managed: true,
        }
      );

      this.wrappedPopup = (new FocusLockInstanceView()).render();

      options.container.append(this.wrappedPopup.el);

      // Waiting for the next cycle to not intercept the click event
      // which may have triggered this showing of the popup.
      // Otherwise, the popup will close without even showing.
      _.defer(() => {
        $(window).on('click', this.clickToDismiss.bind(this));
        if (this.wrappedPopup) {
          this.wrappedPopup.$('.popup__close').focus();
        }
      });
    });
  }

  removePopup() {
    if (this.wrappedPopup) {
      this.wrappedPopup.remove();
      this.popup = null;
      this.wrappedPopup = null;
      $(window).off('click', this.clickToDismiss);
    }
  }

  clickToDismiss(e) {
    if ($(e.target).closest('.popup--uurl-detail').length === 0) {
      this.dismiss();
    }
  }

  dismiss(result) {
    if (this.popup) {
      this.popup.trigger('close', result);
    }
  }
}
